import * as React from 'react';

import { useBoolean, useLockFn } from 'ahooks';
import {
  Badge,
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Select,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { CheckOutlinedIcon, DeleteOutlinedIcon } from 'components';
import { useCategoryValue } from 'context';
import { deleteCategory } from 'services';
import type { CategoryStatus } from 'types';
import { localizeCategoryStatus, PATHNAME } from 'utils';

const {
  LOCAL: {
    CATEGORIES: { BASE_URL: CATEGORIES_BASE_URL },
  },
} = PATHNAME;

const categoryStatuses = ['Active', 'DeActive'] satisfies CategoryStatus[];

type FieldType = Pick<ReturnType<typeof useCategoryValue>, 'status'>;

const CategoryFormAction = () => {
  const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
  const [deleteLoading, { toggle: toggleDeleteLoading }] = useBoolean();
  const { id, defaultStatus, mode } = useCategoryValue();
  const navigate = useNavigate();

  const handleDeleteCategory = useLockFn(
    async ({
      id,
    }: Pick<Required<ReturnType<typeof useCategoryValue>>, 'id'>) => {
      toggleDeleteLoading();

      deleteCategory({ id })
        .then(() => {
          navigate(CATEGORIES_BASE_URL);
          notification.success({
            message: 'دسته‌بندی‌ با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleDeleteLoading();
        });
    },
  );

  const { label: defaultStatusLabel, color: defaultStatusColor } =
    localizeCategoryStatus(defaultStatus);

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار دسته‌بندی‌
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`وضعیت فعلی: ${defaultStatusLabel}`}
            color={defaultStatusColor}
          />
        </Typography.Paragraph>
        <Form.Item<FieldType> label='وضعیت جدید' name='status' className='mb-2'>
          <Select
            allowClear
            placeholder='وضعیت جدید دسته‌بندی‌ را انتخاب کنید'
            options={categoryStatuses.map(status => {
              const { label } = localizeCategoryStatus(status);
              return {
                label,
                value: status,
              };
            })}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        {mode === 'Update' ? (
          <Form.Item>
            <Button
              block
              danger
              className='flex items-center'
              loading={deleteLoading}
              icon={<DeleteOutlinedIcon />}
              onClick={async () => {
                const confirmed = await deleteModalInstance.confirm({
                  title: 'آیا از حذف این دسته‌بندی‌ اطمینان دارید؟',
                  width: 500,
                });
                if (id && confirmed) {
                  handleDeleteCategory({ id });
                }
              }}
            >
              <span>حذف دسته‌بندی‌</span>
            </Button>
            {deleteModalContextHolder}
          </Form.Item>
        ) : null}

        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
          >
            {mode === 'Create' ? (
              <span>افزودن دسته‌بندی‌ جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>
        </Form.Item>
      </div>
    </div>
  );
};

export { CategoryFormAction };
